/**
 * 工作流可视化器
 * 生成工作流的可视化表示，包括流程图和依赖关系
 */

import chalk from 'chalk';
import { WorkflowDefinition, WorkflowStep } from './workflow/workflow-engine';
import { Task } from '../types';
import { CollaborationTask } from './agent-collaboration';
import { logger } from '../utils/logger';

/**
 * 可视化选项
 */
export interface VisualizationOptions {
  format: 'ascii' | 'mermaid' | 'graphviz' | 'json';
  showDetails?: boolean;
  showDependencies?: boolean;
  showConditions?: boolean;
  colorize?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

/**
 * 节点信息
 */
interface NodeInfo {
  id: string;
  label: string;
  type: string;
  status?: string;
  description?: string;
  dependencies?: string[];
  conditions?: string[];
}

/**
 * 边信息
 */
interface EdgeInfo {
  from: string;
  to: string;
  label?: string;
  type?: 'dependency' | 'sequence' | 'condition';
}

/**
 * 工作流可视化器
 */
export class WorkflowVisualizer {
  /**
   * 可视化工作流定义
   */
  visualizeWorkflow(
    workflow: WorkflowDefinition,
    options: VisualizationOptions = { format: 'ascii' }
  ): string {
    switch (options.format) {
      case 'ascii':
        return this.generateAsciiDiagram(workflow, options);
      case 'mermaid':
        return this.generateMermaidDiagram(workflow, options);
      case 'graphviz':
        return this.generateGraphvizDiagram(workflow, options);
      case 'json':
        return this.generateJsonRepresentation(workflow, options);
      default:
        throw new Error(`Unsupported format: ${options.format}`);
    }
  }

  /**
   * 可视化任务依赖关系
   */
  visualizeTaskDependencies(
    tasks: Task[],
    options: VisualizationOptions = { format: 'ascii' }
  ): string {
    const nodes: NodeInfo[] = tasks.map(task => ({
      id: task.id,
      label: task.content.substring(0, 30),
      type: 'task',
      status: task.status,
      description: task.content,
      dependencies: task.dependencies || []
    }));

    const edges: EdgeInfo[] = [];
    tasks.forEach(task => {
      if (task.dependencies) {
        task.dependencies.forEach(dep => {
          edges.push({
            from: dep,
            to: task.id,
            type: 'dependency'
          });
        });
      }
    });

    return this.renderDiagram(nodes, edges, options);
  }

  /**
   * 可视化协作任务
   */
  visualizeCollaboration(
    collaboration: CollaborationTask,
    options: VisualizationOptions = { format: 'ascii' }
  ): string {
    const nodes: NodeInfo[] = collaboration.agents.map((agent, index) => ({
      id: agent.agentId,
      label: agent.role,
      type: 'agent',
      status: collaboration.results?.find(r => r.agentId === agent.agentId) ? 'completed' : 'pending',
      description: agent.task,
      dependencies: agent.dependencies,
      conditions: agent.condition ? ['Has conditions'] : undefined
    }));

    const edges: EdgeInfo[] = [];
    
    // 根据协作类型生成边
    if (collaboration.type === 'sequential') {
      for (let i = 0; i < nodes.length - 1; i++) {
        edges.push({
          from: nodes[i].id,
          to: nodes[i + 1].id,
          type: 'sequence'
        });
      }
    } else if (collaboration.type === 'parallel') {
      // 并行执行，创建一个虚拟起始节点
      nodes.unshift({
        id: 'start',
        label: 'Start',
        type: 'control'
      });
      nodes.push({
        id: 'end',
        label: 'End',
        type: 'control'
      });

      collaboration.agents.forEach(agent => {
        edges.push({ from: 'start', to: agent.agentId, type: 'sequence' });
        edges.push({ from: agent.agentId, to: 'end', type: 'sequence' });
      });
    }

    // 添加依赖边
    collaboration.agents.forEach(agent => {
      if (agent.dependencies) {
        agent.dependencies.forEach(dep => {
          edges.push({
            from: dep,
            to: agent.agentId,
            type: 'dependency',
            label: 'depends on'
          });
        });
      }
    });

    return this.renderDiagram(nodes, edges, options);
  }

  /**
   * 生成 ASCII 图表
   */
  private generateAsciiDiagram(
    workflow: WorkflowDefinition,
    options: VisualizationOptions
  ): string {
    const nodes: NodeInfo[] = workflow.steps.map(step => ({
      id: step.id,
      label: step.name,
      type: step.type,
      description: step.description,
      conditions: step.condition ? [this.formatCondition(step.condition)] : undefined
    }));

    const edges: EdgeInfo[] = [];
    
    // 生成顺序边
    for (let i = 0; i < workflow.steps.length - 1; i++) {
      edges.push({
        from: workflow.steps[i].id,
        to: workflow.steps[i + 1].id,
        type: 'sequence'
      });
    }

    return this.renderAsciiDiagram(nodes, edges, options);
  }

  /**
   * 生成 Mermaid 图表
   */
  private generateMermaidDiagram(
    workflow: WorkflowDefinition,
    options: VisualizationOptions
  ): string {
    const lines: string[] = ['graph TD'];
    
    // 定义节点
    workflow.steps.forEach(step => {
      const shape = this.getMermaidShape(step.type);
      const label = options.showDetails 
        ? `${step.name}<br/>${step.description || ''}`
        : step.name;
      lines.push(`    ${step.id}${shape[0]}${label}${shape[1]}`);
    });

    // 定义边
    for (let i = 0; i < workflow.steps.length - 1; i++) {
      const from = workflow.steps[i];
      const to = workflow.steps[i + 1];
      
      if (from.condition && options.showConditions) {
        lines.push(`    ${from.id} -->|${this.formatCondition(from.condition)}| ${to.id}`);
      } else {
        lines.push(`    ${from.id} --> ${to.id}`);
      }
    }

    return lines.join('\n');
  }

  /**
   * 生成 Graphviz 图表
   */
  private generateGraphvizDiagram(
    workflow: WorkflowDefinition,
    options: VisualizationOptions
  ): string {
    const lines: string[] = [
      'digraph workflow {',
      '    rankdir=' + (options.orientation === 'horizontal' ? 'LR' : 'TB') + ';',
      '    node [shape=box];'
    ];

    // 定义节点
    workflow.steps.forEach(step => {
      const attrs: string[] = [`label="${step.name}"`];
      if (step.type === 'action') attrs.push('style=filled', 'fillcolor=lightblue');
      if (step.type === 'condition') attrs.push('shape=diamond');
      lines.push(`    ${step.id} [${attrs.join(', ')}];`);
    });

    // 定义边
    for (let i = 0; i < workflow.steps.length - 1; i++) {
      const from = workflow.steps[i];
      const to = workflow.steps[i + 1];
      
      if (from.condition && options.showConditions) {
        lines.push(`    ${from.id} -> ${to.id} [label="${this.formatCondition(from.condition)}"];`);
      } else {
        lines.push(`    ${from.id} -> ${to.id};`);
      }
    }

    lines.push('}');
    return lines.join('\n');
  }

  /**
   * 生成 JSON 表示
   */
  private generateJsonRepresentation(
    workflow: WorkflowDefinition,
    options: VisualizationOptions
  ): string {
    const nodes = workflow.steps.map(step => ({
      id: step.id,
      name: step.name,
      type: step.type,
      description: step.description,
      condition: step.condition
    }));

    const edges: any[] = [];
    for (let i = 0; i < workflow.steps.length - 1; i++) {
      edges.push({
        from: workflow.steps[i].id,
        to: workflow.steps[i + 1].id,
        type: 'sequence'
      });
    }

    return JSON.stringify({ nodes, edges }, null, 2);
  }

  /**
   * 渲染图表
   */
  private renderDiagram(
    nodes: NodeInfo[],
    edges: EdgeInfo[],
    options: VisualizationOptions
  ): string {
    switch (options.format) {
      case 'ascii':
        return this.renderAsciiDiagram(nodes, edges, options);
      case 'mermaid':
        return this.renderMermaidDiagram(nodes, edges, options);
      case 'graphviz':
        return this.renderGraphvizDiagram(nodes, edges, options);
      case 'json':
        return JSON.stringify({ nodes, edges }, null, 2);
      default:
        return this.renderAsciiDiagram(nodes, edges, options);
    }
  }

  /**
   * 渲染 ASCII 图表
   */
  private renderAsciiDiagram(
    nodes: NodeInfo[],
    edges: EdgeInfo[],
    options: VisualizationOptions
  ): string {
    const lines: string[] = [];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // 构建简单的 ASCII 表示
    if (options.orientation === 'horizontal') {
      // 水平布局
      const nodeStrs = nodes.map(node => {
        const box = this.createAsciiBox(node.label, node.type, options);
        return box;
      });

      // 显示节点
      lines.push(nodeStrs.join(' → '));
      
      // 显示详细信息
      if (options.showDetails) {
        lines.push('');
        nodes.forEach(node => {
          lines.push(`${chalk.bold(node.label)}:`);
          if (node.description) lines.push(`  描述: ${node.description}`);
          if (node.status) lines.push(`  状态: ${this.formatStatus(node.status, options)}`);
          if (node.dependencies?.length) lines.push(`  依赖: ${node.dependencies.join(', ')}`);
          if (node.conditions?.length) lines.push(`  条件: ${node.conditions.join(', ')}`);
        });
      }
    } else {
      // 垂直布局
      nodes.forEach((node, index) => {
        const box = this.createAsciiBox(node.label, node.type, options);
        lines.push(box);
        
        if (options.showDetails) {
          if (node.description) lines.push(`  ${node.description}`);
          if (node.status) lines.push(`  状态: ${this.formatStatus(node.status, options)}`);
        }
        
        // 添加连接线
        if (index < nodes.length - 1) {
          const hasEdge = edges.some(e => e.from === node.id);
          if (hasEdge) {
            lines.push('    ↓');
          }
        }
      });
    }

    // 显示依赖关系
    if (options.showDependencies && edges.some(e => e.type === 'dependency')) {
      lines.push('');
      lines.push('依赖关系:');
      edges.filter(e => e.type === 'dependency').forEach(edge => {
        const from = nodeMap.get(edge.from);
        const to = nodeMap.get(edge.to);
        if (from && to) {
          lines.push(`  ${from.label} → ${to.label}${edge.label ? ` (${edge.label})` : ''}`);
        }
      });
    }

    return lines.join('\n');
  }

  /**
   * 渲染 Mermaid 图表
   */
  private renderMermaidDiagram(
    nodes: NodeInfo[],
    edges: EdgeInfo[],
    options: VisualizationOptions
  ): string {
    const lines: string[] = ['graph ' + (options.orientation === 'horizontal' ? 'LR' : 'TD')];
    
    // 定义节点
    nodes.forEach(node => {
      const shape = this.getMermaidShape(node.type);
      const label = options.showDetails && node.description
        ? `${node.label}<br/>${node.description}`
        : node.label;
      lines.push(`    ${node.id}${shape[0]}${label}${shape[1]}`);
    });

    // 定义边
    edges.forEach(edge => {
      const arrow = edge.type === 'dependency' ? '-..->' : '-->';
      const label = edge.label ? `|${edge.label}|` : '';
      lines.push(`    ${edge.from} ${arrow}${label} ${edge.to}`);
    });

    return lines.join('\n');
  }

  /**
   * 渲染 Graphviz 图表
   */
  private renderGraphvizDiagram(
    nodes: NodeInfo[],
    edges: EdgeInfo[],
    options: VisualizationOptions
  ): string {
    const lines: string[] = [
      'digraph G {',
      '    rankdir=' + (options.orientation === 'horizontal' ? 'LR' : 'TB') + ';'
    ];

    // 定义节点样式
    const nodeStyles: Record<string, string[]> = {
      task: ['shape=box', 'style=rounded'],
      agent: ['shape=box', 'style=filled', 'fillcolor=lightblue'],
      control: ['shape=diamond'],
      workflow: ['shape=box', 'style=filled', 'fillcolor=lightgreen']
    };

    // 定义节点
    nodes.forEach(node => {
      const style = nodeStyles[node.type] || ['shape=box'];
      const label = options.showDetails && node.description
        ? `${node.label}\\n${node.description}`
        : node.label;
      lines.push(`    "${node.id}" [label="${label}", ${style.join(', ')}];`);
    });

    // 定义边
    edges.forEach(edge => {
      const style = edge.type === 'dependency' ? 'style=dashed' : '';
      const label = edge.label ? `label="${edge.label}"` : '';
      const attrs = [style, label].filter(Boolean).join(', ');
      lines.push(`    "${edge.from}" -> "${edge.to}"${attrs ? ` [${attrs}]` : ''};`);
    });

    lines.push('}');
    return lines.join('\n');
  }

  /**
   * 创建 ASCII 方框
   */
  private createAsciiBox(label: string, type: string, options: VisualizationOptions): string {
    const maxLen = Math.max(label.length * 2, 10); // 中文字符占两个宽度
    const padding = Math.max(0, Math.floor((maxLen - label.length * 2) / 2));
    
    let box = `┌${'─'.repeat(maxLen)}┐\n`;
    box += `│${' '.repeat(padding)}${label}${' '.repeat(maxLen - padding - label.length * 2)}│\n`;
    box += `└${'─'.repeat(maxLen)}┘`;

    if (options.colorize) {
      switch (type) {
        case 'task':
          return chalk.blue(box);
        case 'agent':
          return chalk.cyan(box);
        case 'control':
          return chalk.yellow(box);
        case 'workflow':
          return chalk.green(box);
        default:
          return box;
      }
    }

    return box;
  }

  /**
   * 获取 Mermaid 形状
   */
  private getMermaidShape(type: string): [string, string] {
    switch (type) {
      case 'condition':
        return ['{', '}'];
      case 'action':
        return ['[', ']'];
      case 'control':
        return ['((', '))'];
      default:
        return ['[', ']'];
    }
  }

  /**
   * 格式化条件
   */
  private formatCondition(condition: any): string {
    if (typeof condition === 'string') return condition;
    if (condition.expression) return condition.expression;
    if (condition.type) return condition.type;
    return 'condition';
  }

  /**
   * 格式化状态
   */
  private formatStatus(status: string, options: VisualizationOptions): string {
    if (!options.colorize) return status;

    switch (status) {
      case 'completed':
      case 'done':
        return chalk.green(status);
      case 'in_progress':
      case 'running':
        return chalk.yellow(status);
      case 'failed':
      case 'error':
        return chalk.red(status);
      case 'pending':
      case 'todo':
        return chalk.gray(status);
      default:
        return status;
    }
  }

  /**
   * 导出为文件
   */
  async exportToFile(
    content: string,
    filename: string,
    format: string
  ): Promise<void> {
    const fs = await import('fs-extra');
    const path = await import('path');
    
    const ext = format === 'mermaid' ? 'mmd' : format === 'graphviz' ? 'dot' : format;
    const filepath = filename.endsWith(`.${ext}`) ? filename : `${filename}.${ext}`;
    
    await fs.writeFile(filepath, content, 'utf-8');
    logger.info(`Visualization exported to: ${path.resolve(filepath)}`);
  }
}